import {
    Button,
    Modal,
    ModalDialog,
    ModalClose,
    FormControl,
    FormLabel,
    Input,
    Stack,
    DialogTitle,
    DialogContent,
    Switch,
    FormHelperText,
    Typography,
    Box,
} from "@mui/joy";
import { generateClient } from "aws-amplify/api";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createRoom } from "../../graphql/mutations";
import { useModalContext } from "../../context/modalContext";
import { getCurrentUser } from "aws-amplify/auth";
import { useMutation } from "react-query";
import { FieldApi, useForm } from "@tanstack/react-form";
import { roomByName } from "../../graphql/queries";
import { MeetingRoom } from "@mui/icons-material";

function FieldInfo({ field }: { field: FieldApi<any, any, any, any> }) {
    return (
        <>
            {field.state.meta.touchedErrors ? (
                <FormHelperText sx={{ color: "danger.500" }}>{field.state.meta.touchedErrors}</FormHelperText>
            ) : null}
            {field.state.meta.isValidating ? <FormHelperText>Validating...</FormHelperText> : null}
        </>
    );
}

interface NewRoom {
    name: string;
    private: boolean;
    allowComments: boolean;
    private_code?: string;
}

export const CreateRoomModal = () => {
    const navigate = useNavigate();
    const client = generateClient();
    const { createRoomModalOpen, setCreateRoomModalOpen, setNeedsRefetch } = useModalContext();
    const [showPasscode, setShowPasscode] = useState(false);

    const createRoomMutation = async (newRoom: NewRoom) => {
        const { userId } = await getCurrentUser();
        const { data } = await client.graphql({
            query: createRoom,
            variables: { input: { ...newRoom, user_id: userId } },
        });
        return data.createRoom;
    };

    const { mutate, isLoading } = useMutation(createRoomMutation, {
        onSuccess: (data) => {
            setCreateRoomModalOpen(false);
            setNeedsRefetch(true);
            navigate("/room/" + data.name);
        },
        onError: (error) => {
            console.error("Error creating room:", error);
        },
    });

    const form = useForm({
        defaultValues: {
            name: "",
            privateRoom: false,
            privateRoomPassword: Math.floor(100000 + Math.random() * 900000).toString(),
            allowComments: true,
        },
        onSubmit: async ({ value }) => {
            mutate({
                name: value.name,
                private: value.privateRoom,
                allowComments: value.allowComments,
                private_code: value.privateRoom ? value.privateRoomPassword : undefined,
            });
            setShowPasscode(false);
        },
    });

    return (
        <Modal
            open={createRoomModalOpen}
            sx={{ zIndex: 100000000 }}
            onClose={() => {
                setCreateRoomModalOpen(false);
                setShowPasscode(false);
            }}
        >
            <ModalDialog
                variant="outlined"
                sx={{
                    borderRadius: "xl",
                    p: 3,
                    boxShadow: "lg",
                    maxWidth: 440,
                    width: "100%",
                }}
            >
                <ModalClose />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "lg",
                            bgcolor: "primary.softBg",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.600",
                        }}
                    >
                        <MeetingRoom sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                        <DialogTitle sx={{ p: 0 }}>Create Room</DialogTitle>
                        <Typography level="body-sm" textColor="text.secondary">
                            Set up your delivery room
                        </Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ mt: 2 }}>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <Stack spacing={2.5}>
                            <form.Field
                                name="name"
                                validators={{
                                    onSubmitAsync: async ({ value }) => {
                                        const isValid = /^[a-zA-Z0-9-]+$/.test(value);
                                        const isValidMultipleDashes = /^(?!.*--)(?!^-)(?!.*-$)[a-zA-Z0-9-]+$/.test(value);

                                        if (!isValid) {
                                            return "Only letters, numbers, and dashes are allowed.";
                                        }
                                        if (!isValidMultipleDashes) {
                                            return "No leading or trailing dashes, and no multiple dashes in a row.";
                                        }

                                        try {
                                            const { data } = await client.graphql({
                                                query: roomByName,
                                                variables: { name: value, limit: 1 },
                                            });
                                            if (data.roomByName.items.length > 0) {
                                                return "A room with this name already exists.";
                                            }
                                        } catch (error) {
                                            console.error("Error checking room name:", error);
                                            return "An error occurred while checking the room name.";
                                        }

                                        return undefined;
                                    },
                                }}
                                children={(field) => {
                                    return (
                                        <FormControl required error={field.state.meta.errors.length > 0}>
                                            <FormLabel>Room Name</FormLabel>
                                            <Input
                                                id={field.name}
                                                size="lg"
                                                autoFocus
                                                required
                                                value={field.state.value}
                                                onChange={(e) => field.handleChange(e.target.value.toLowerCase())}
                                                placeholder="baby-nuckols"
                                            />
                                            <FormHelperText>
                                                This will be part of your room's URL
                                            </FormHelperText>
                                            <FieldInfo field={field} />
                                        </FormControl>
                                    );
                                }}
                            />
                            <form.Field
                                name="privateRoom"
                                children={(field) => (
                                    <FormControl
                                        orientation="horizontal"
                                        sx={{
                                            justifyContent: "space-between",
                                            p: 2,
                                            borderRadius: "lg",
                                            bgcolor: "background.level1",
                                        }}
                                    >
                                        <Box>
                                            <FormLabel sx={{ m: 0 }}>Private Room</FormLabel>
                                            <FormHelperText sx={{ mt: 0.5 }}>
                                                Require a passcode to join
                                            </FormHelperText>
                                        </Box>
                                        <Switch
                                            checked={field.state.value}
                                            onChange={(event) => {
                                                field.handleChange(event.target.checked);
                                                setShowPasscode(event.target.checked);
                                            }}
                                        />
                                    </FormControl>
                                )}
                            />
                            {showPasscode && (
                                <form.Field
                                    name="privateRoomPassword"
                                    validators={{
                                        onSubmit: ({ value }) => {
                                            const isValid = /^\d{4}(\d{2})?$/.test(value);
                                            return !isValid ? "Must be a 4 or 6 digit number." : undefined;
                                        },
                                    }}
                                    children={(field) => (
                                        <FormControl required={showPasscode} error={field.state.meta.errors.length > 0}>
                                            <FormLabel>Passcode</FormLabel>
                                            <Input
                                                type="number"
                                                size="lg"
                                                value={field.state.value}
                                                onChange={(e) => field.handleChange(e.target.value)}
                                            />
                                            <FormHelperText>Share this code with people you want to access the room</FormHelperText>
                                            <FieldInfo field={field} />
                                        </FormControl>
                                    )}
                                />
                            )}
                            <form.Field
                                name="allowComments"
                                children={(field) => (
                                    <FormControl
                                        orientation="horizontal"
                                        sx={{
                                            justifyContent: "space-between",
                                            p: 2,
                                            borderRadius: "lg",
                                            bgcolor: "background.level1",
                                        }}
                                    >
                                        <Box>
                                            <FormLabel sx={{ m: 0 }}>Allow Comments</FormLabel>
                                            <FormHelperText sx={{ mt: 0.5 }}>
                                                Let guests leave comments on updates
                                            </FormHelperText>
                                            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                                Only you and the commenter can see comments
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={field.state.value}
                                            onChange={(event) => field.handleChange(event.target.checked)}
                                        />
                                    </FormControl>
                                )}
                            />
                            <Button type="submit" size="lg" loading={isLoading} sx={{ mt: 1 }}>
                                Create Room
                            </Button>
                        </Stack>
                    </form>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
};
